<template>
  <div class="welcome-container">
    <div class="animated-bg"></div>
    <div class="welcome-content">

      <!-- Hero Section -->
      <div class="hero-section glass-card">
        <div class="hero-badge">
          <v-icon small>mdi-lightning-bolt</v-icon>
          <span>Breaking News Analysis in Under 10 Seconds</span>
        </div>
        <h1 class="hero-title">
          <span class="gradient-text">The Future of</span>
          <br />
          <span class="highlight-text">Unbiased News Analysis</span>
        </h1>
        <p class="hero-subtitle">
          Transform any breaking news story into comprehensive, multi-expert video conversations
          with balanced perspectives and full citations—faster than any traditional newsroom.
        </p>

        <div class="hero-cta">
          <v-btn
            color="primary"
            x-large
            rounded
            elevation="0"
            @click="startAnalysis"
            class="primary-cta glass-button"
          >
            <v-icon left>mdi-play-circle</v-icon>
            Analyze Breaking News Now
          </v-btn>
        </div>

        <!-- Impact Stats -->
        <div class="impact-stats">
          <div class="stat-item">
            <span class="stat-number">30K+</span>
            <span class="stat-label">Weekly Impressions</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">93%</span>
            <span class="stat-label">Algorithmic Reach</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">10s</span>
            <span class="stat-label">Analysis Speed</span>
          </div>
        </div>
      </div>

      <!-- Problem & Solution Section -->
      <div class="problem-solution-section glass-card">
        <div class="section-content">
          <div class="problem-side">
            <div class="section-icon problem-icon">
              <v-icon size="48" color="error">mdi-alert-circle</v-icon>
            </div>
            <h3 class="section-subtitle">The Problem</h3>
            <ul class="problem-list">
              <li>Traditional news is biased and slow</li>
              <li>Complex issues lack multi-perspective analysis</li>
              <li>Breaking news takes hours/days for expert commentary</li>
              <li>Audiences can't access balanced viewpoints quickly</li>
            </ul>
          </div>

          <div class="solution-side">
            <div class="section-icon solution-icon">
              <v-icon size="48" color="success">mdi-check-circle</v-icon>
            </div>
            <h3 class="section-subtitle">Our Solution</h3>
            <ul class="solution-list">
              <li>Proprietary ECRAG technology for unbiased analysis</li>
              <li>Multi-expert perspectives with full citations</li>
              <li>10-second transformation from news to video analysis</li>
              <li>Balanced, expert-driven content at breaking news speed</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Technology Section -->
      <div class="technology-section">
        <h2 class="section-title gradient-text">ECRAG Technology</h2>
        <div class="tech-grid">
          <div class="tech-card glass-card">
            <div class="tech-icon">
              <v-icon color="primary" size="40">mdi-brain</v-icon>
            </div>
            <h4>Entity-Centric Analysis</h4>
            <p>Advanced AI modeling that understands context and relationships in complex news stories</p>
          </div>

          <div class="tech-card glass-card">
            <div class="tech-icon">
              <v-icon color="secondary" size="40">mdi-database-search</v-icon>
            </div>
            <h4>Retrieval-Augmented Generation</h4>
            <p>Real-time research across authoritative sources with transparent citation tracking</p>
          </div>

          <div class="tech-card glass-card">
            <div class="tech-icon">
              <v-icon color="success" size="40">mdi-account-group</v-icon>
            </div>
            <h4>Multi-Perspective Modeling</h4>
            <p>Dynamic expert personas that provide balanced viewpoints on complex issues</p>
          </div>
        </div>
      </div>

      <!-- How It Works -->
      <div class="workflow-section">
        <h2 class="section-title gradient-text">From Breaking News to Expert Analysis</h2>
        <div class="workflow-timeline">
          <div class="workflow-item">
            <div class="workflow-icon">
              <v-icon>mdi-newspaper</v-icon>
            </div>
            <div class="workflow-content">
              <h4>Submit News Topic</h4>
              <p>Enter any breaking news story or complex issue</p>
            </div>
          </div>
          <div class="workflow-arrow">→</div>
          <div class="workflow-item">
            <div class="workflow-icon">
              <v-icon>mdi-magnify</v-icon>
            </div>
            <div class="workflow-content">
              <h4>ECRAG Research</h4>
              <p>AI researches multiple authoritative sources</p>
            </div>
          </div>
          <div class="workflow-arrow">→</div>
          <div class="workflow-item">
            <div class="workflow-icon">
              <v-icon>mdi-chat-processing</v-icon>
            </div>
            <div class="workflow-content">
              <h4>Generate Expert Dialogue</h4>
              <p>Creates balanced multi-perspective conversations</p>
            </div>
          </div>
          <div class="workflow-arrow">→</div>
          <div class="workflow-item">
            <div class="workflow-icon">
              <v-icon>mdi-video</v-icon>
            </div>
            <div class="workflow-content">
              <h4>Instant Video Analysis</h4>
              <p>Professional video ready in under 10 seconds</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Key Features -->
      <div class="features-section glass-card">
        <h2 class="section-title gradient-text">Why UnStruct.AI</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">
              <v-icon color="primary" size="48">mdi-speed</v-icon>
            </div>
            <h3 class="feature-title">Faster Than Breaking News</h3>
            <p class="feature-desc">
              Generate comprehensive analysis in under 10 seconds—faster than any traditional newsroom can respond to breaking stories.
            </p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <v-icon color="success" size="48">mdi-scale-balance</v-icon>
            </div>
            <h3 class="feature-title">Truly Unbiased</h3>
            <p class="feature-desc">
              Our ECRAG system presents multiple expert perspectives without editorial bias, letting you form your own informed opinions.
            </p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <v-icon color="secondary" size="48">mdi-source-branch</v-icon>
            </div>
            <h3 class="feature-title">Full Source Transparency</h3>
            <p class="feature-desc">
              Every claim is backed by authoritative sources with complete citations—fact-check everything instantly.
            </p>
          </div>
        </div>
      </div>

      <!-- Use Cases -->
      <div class="use-cases-section">
        <h2 class="section-title gradient-text">Perfect For</h2>
        <div class="use-cases-grid">
          <div
            v-for="(useCase, index) in useCases"
            :key="index"
            class="use-case-card glass-card"
          >
            <div class="use-case-icon">
              <v-icon :color="useCase.color" size="36">{{ useCase.icon }}</v-icon>
            </div>
            <h4>{{ useCase.title }}</h4>
            <p>{{ useCase.description }}</p>
          </div>
        </div>
      </div>

      <!-- Vision Section -->
      <div class="vision-section glass-card">
        <h2 class="section-title gradient-text">Our Vision</h2>
        <div class="vision-content">
          <p class="vision-text">
            Building toward a future where anyone can access balanced, expert analysis of any breaking news story within seconds—creating a new standard for informed public discourse that transcends traditional media limitations.
          </p>
          <div class="vision-stats">
            <div class="vision-stat">
              <h4>Democratizing</h4>
              <p>Expert-level news analysis</p>
            </div>
            <div class="vision-stat">
              <h4>Eliminating</h4>
              <p>Media bias and echo chambers</p>
            </div>
            <div class="vision-stat">
              <h4>Accelerating</h4>
              <p>Informed public discourse</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom CTA -->
      <div class="bottom-cta glass-card">
        <h2 class="cta-title">Experience the Future of News Analysis</h2>
        <p class="cta-subtitle">Join the movement toward unbiased, expert-driven journalism</p>
        <v-btn
          color="primary"
          x-large
          rounded
          @click="startAnalysis"
          class="final-cta"
        >
          Start Analyzing News Now
        </v-btn>
        <div class="trust-indicators">
          <div class="trust-item">
            <v-icon small>mdi-shield-check</v-icon>
            <span>Unbiased Analysis</span>
          </div>
          <div class="trust-item">
            <v-icon small>mdi-source-branch</v-icon>
            <span>Full Citations</span>
          </div>
          <div class="trust-item">
            <v-icon small>mdi-lightning-bolt</v-icon>
            <span>10-Second Speed</span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'UnstructNewsWelcome',
  data() {
    return {
      useCases: [
        {
          title: "Journalists & Newsrooms",
          description: "Get multi-perspective analysis faster than breaking news",
          icon: "mdi-newspaper",
          color: "primary"
        },
        {
          title: "Policy Makers",
          description: "Understand complex issues with balanced expert insights",
          icon: "mdi-account-tie",
          color: "secondary"
        },
        {
          title: "Researchers & Analysts",
          description: "Access comprehensive viewpoints for thorough analysis",
          icon: "mdi-chart-line",
          color: "success"
        },
        {
          title: "Educators",
          description: "Teach critical thinking with unbiased news analysis",
          icon: "mdi-school",
          color: "warning"
        },
        {
          title: "Business Leaders",
          description: "Stay informed on events affecting your industry",
          icon: "mdi-briefcase",
          color: "info"
        },
        {
          title: "Informed Citizens",
          description: "Cut through bias to understand what's really happening",
          icon: "mdi-account-group",
          color: "error"
        }
      ]
    };
  },
  methods: {
    startAnalysis() {
      this.$emit('start-first-project');
    }
  }
};
</script>

<style scoped>
/* Base Container */
.welcome-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
}

/* Animated Background */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.1;
  z-index: 0;
}

.animated-bg::before,
.animated-bg::after {
  content: '';
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 234, 255, 0.3) 0%, transparent 70%);
  animation: float 20s infinite ease-in-out;
}

.animated-bg::before {
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.animated-bg::after {
  bottom: -200px;
  right: -200px;
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-20px, -50px) rotate(120deg); }
  66% { transform: translate(20px, -20px) rotate(240deg); }
}

/* Glass Card Effect */
.glass-card {
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* Welcome Content */
.welcome-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 60px 40px;
  margin-bottom: 60px;
  position: relative;
  overflow: hidden;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 30px;
  animation: slideDown 0.6s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-title {
  font-size: clamp(36px, 6vw, 72px);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 30px;
  animation: fadeInUp 0.8s ease-out;
}

.gradient-text {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.highlight-text {
  color: #f9fafb;
  display: inline-block;
  font-weight: 800;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-subtitle {
  font-size: 20px;
  color: #d1d5db;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.6;
  animation: fadeIn 1s ease-out 0.3s both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Impact Stats */
.impact-stats {
  display: flex;
  justify-content: center;
  gap: 60px;
  margin-top: 50px;
  flex-wrap: wrap;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 36px;
  font-weight: 800;
  color: #00eaff;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #9ca3af;
  font-weight: 500;
}

/* Hero CTA */
.hero-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  animation: fadeIn 1s ease-out 0.5s both;
  margin-bottom: 20px;
}

.primary-cta {
  padding: 16px 40px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease !important;
}

.glass-button {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

.glass-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 234, 255, 0.3) !important;
}

/* Problem Solution Section */
.problem-solution-section {
  padding: 60px 40px;
  margin-bottom: 60px;
}

.section-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
}

.section-icon {
  margin-bottom: 20px;
}

.section-subtitle {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #f9fafb;
}

.problem-list, .solution-list {
  list-style: none;
  padding: 0;
}

.problem-list li, .solution-list li {
  padding: 12px 0;
  position: relative;
  padding-left: 28px;
  font-size: 16px;
  color: #d1d5db;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.problem-list li:last-child, .solution-list li:last-child {
  border-bottom: none;
}

.problem-list li::before {
  content: '×';
  position: absolute;
  left: 0;
  color: #ef4444;
  font-size: 20px;
  font-weight: bold;
}

.solution-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #10b981;
  font-size: 16px;
  font-weight: bold;
}

/* Technology Section */
.technology-section {
  margin: 100px 0;
}

.section-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  padding: 0 20px;
}

.tech-card {
  padding: 40px 30px;
  text-align: center;
  transition: all 0.3s ease;
}

.tech-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
}

.tech-icon {
  margin-bottom: 20px;
}

.tech-card h4 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #f9fafb;
}

.tech-card p {
  color: #d1d5db;
  line-height: 1.6;
  margin: 0;
}

/* Workflow Section */
.workflow-section {
  margin: 100px 0;
  text-align: center;
}

.workflow-timeline {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 60px;
  flex-wrap: wrap;
}

.workflow-item {
  flex: 1;
  min-width: 200px;
  max-width: 250px;
  text-align: center;
}

.workflow-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
}

.workflow-content h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #f9fafb;
}

.workflow-content p {
  color: #d1d5db;
  font-size: 14px;
  margin: 0;
}

.workflow-arrow {
  font-size: 36px;
  color: #00eaff;
  opacity: 0.7;
}

/* Features Section */
.features-section {
  padding: 60px 40px;
  margin-bottom: 60px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.feature-card {
  background: rgba(30, 41, 59, 0.5);
  border-radius: 16px;
  padding: 30px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
}

.feature-icon {
  margin-bottom: 20px;
}

.feature-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #f9fafb;
}

.feature-desc {
  color: #d1d5db;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
}

/* Use Cases Section */
.use-cases-section {
  margin: 100px 0;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  padding: 0 20px;
}

.use-case-card {
  padding: 30px;
  text-align: center;
  position: relative;
  transition: all 0.4s ease;
  overflow: hidden;
}

.use-case-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.use-case-icon {
  margin-bottom: 20px;
}

.use-case-card h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #f9fafb;
}

.use-case-card p {
  color: #d1d5db;
  font-size: 14px;
  margin: 0;
}

/* Vision Section */
.vision-section {
  padding: 60px 40px;
  margin: 100px 0;
  text-align: center;
}

.vision-content {
  max-width: 800px;
  margin: 0 auto;
}

.vision-text {
  font-size: 20px;
  line-height: 1.6;
  color: #d1d5db;
  margin-bottom: 40px;
}

.vision-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.vision-stat {
  text-align: center;
}

.vision-stat h4 {
  font-size: 20px;
  font-weight: 700;
  color: #00eaff;
  margin-bottom: 8px;
}

.vision-stat p {
  color: #9ca3af;
  font-size: 14px;
  margin: 0;
}

/* Bottom CTA */
.bottom-cta {
  text-align: center;
  padding: 80px 40px;
  margin: 100px 0 40px;
}

.cta-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #f9fafb;
  line-height: 1.2;
}

.cta-subtitle {
  font-size: 20px;
  color: #d1d5db;
  margin-bottom: 40px;
}

.final-cta {
  padding: 18px 48px !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  margin-top: 30px;
  box-shadow: 0 8px 24px rgba(0, 234, 255, 0.3) !important;
  text-transform: none !important;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%) !important;
  color: white !important;
}

.trust-indicators {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 40px;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #d1d5db;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .section-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .workflow-timeline {
    gap: 10px;
  }

  .workflow-item {
    min-width: 180px;
  }

  .workflow-arrow {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .welcome-content {
    padding: 20px;
  }

  .hero-section, .problem-solution-section, .features-section, .vision-section {
    padding: 40px 20px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .impact-stats {
    gap: 30px;
  }

  .stat-number {
    font-size: 28px;
  }

  .tech-grid {
    grid-template-columns: 1fr;
  }

  .workflow-timeline {
    flex-direction: column;
  }

  .workflow-arrow {
    transform: rotate(90deg);
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .use-cases-grid {
    grid-template-columns: 1fr;
  }

  .bottom-cta {
    padding: 60px 30px;
  }

  .cta-title {
    font-size: 28px;
  }

  .trust-indicators {
    flex-direction: column;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .section-title {
    font-size: 24px;
  }

  .glass-card {
    border-radius: 16px;
    padding: 20px;
  }

  .workflow-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }

  .impact-stats {
    flex-direction: column;
    gap: 20px;
  }
}

/* Smooth Scroll */
html {
  scroll-behavior: smooth;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 234, 255, 0.4);
  border-radius: 4px;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 234, 255, 0.6);
}
</style>
